/**
 * Timezone helpers for booking meetings.
 *
 * The scheduler lets the host pick a wall-clock time in whatever zone the client
 * lives in, so every conversion has to go through an explicit IANA zone rather
 * than the browser's local zone. `Intl` already knows every zone's offset and
 * DST rules, so we use it as the source of truth instead of shipping a library.
 */

// Team zones first (Miami / Buenos Aires / Kyiv / São Paulo), then the zones
// clients are usually in.
export const TIMEZONES = [
  { value: "America/New_York", label: "Miami / New York (Eastern)" },
  { value: "America/Chicago", label: "Chicago (Central)" },
  { value: "America/Denver", label: "Denver (Mountain)" },
  { value: "America/Phoenix", label: "Phoenix (Arizona)" },
  { value: "America/Los_Angeles", label: "Los Angeles (Pacific)" },
  { value: "America/Toronto", label: "Toronto" },
  { value: "America/Vancouver", label: "Vancouver" },
  { value: "America/Mexico_City", label: "Mexico City" },
  { value: "America/Bogota", label: "Bogotá" },
  { value: "America/Sao_Paulo", label: "São Paulo" },
  { value: "America/Argentina/Buenos_Aires", label: "Buenos Aires" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Lisbon", label: "Lisbon" },
  { value: "Europe/Madrid", label: "Madrid" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Europe/Kiev", label: "Kyiv" },
  { value: "Europe/Istanbul", label: "Istanbul" },
  { value: "Asia/Jerusalem", label: "Tel Aviv" },
  { value: "Asia/Dubai", label: "Dubai" },
  { value: "Asia/Kolkata", label: "India" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Shanghai", label: "Shanghai" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "Pacific/Auckland", label: "Auckland" },
  { value: "UTC", label: "UTC" },
];

export const DEFAULT_TIMEZONE = "America/New_York";

/** The browser's zone, but only if we actually offer it in the picker. */
export function detectTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONES.some((t) => t.value === tz) ? tz : DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

const PART_FORMATTERS = new Map();

function partsFormatter(timeZone) {
  if (!PART_FORMATTERS.has(timeZone)) {
    PART_FORMATTERS.set(
      timeZone,
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    );
  }
  return PART_FORMATTERS.get(timeZone);
}

function zonedParts(instant, timeZone) {
  return partsFormatter(timeZone)
    .formatToParts(instant)
    .reduce((acc, p) => {
      if (p.type !== "literal") acc[p.type] = p.value;
      return acc;
    }, {});
}

/** How far `timeZone` is ahead of UTC at the given instant, in ms. */
function offsetMs(instant, timeZone) {
  const parts = zonedParts(instant, timeZone);
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24, // some engines render midnight as hour 24
    Number(parts.minute),
    Number(parts.second)
  );
  return asUTC - instant.getTime();
}

/**
 * "2026-08-10" + "15:00" read as wall time in `timeZone` -> UTC ISO string.
 * Two passes so times near a DST change resolve against the right offset.
 */
export function wallTimeToUtcIso(dateStr, timeStr, timeZone) {
  if (!dateStr || !timeStr) return null;
  const naive = Date.parse(`${dateStr}T${timeStr}:00Z`);
  if (Number.isNaN(naive)) return null;
  const tz = timeZone || DEFAULT_TIMEZONE;
  let guess = naive - offsetMs(new Date(naive), tz);
  guess = naive - offsetMs(new Date(guess), tz);
  return new Date(guess).toISOString();
}

/** UTC ISO -> { date: "YYYY-MM-DD", time: "HH:MM" } as read in `timeZone`. */
export function utcIsoToWallTime(iso, timeZone) {
  if (!iso) return { date: "", time: "" };
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return { date: "", time: "" };
  const parts = zonedParts(instant, timeZone || DEFAULT_TIMEZONE);
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${String(Number(parts.hour) % 24).padStart(2, "0")}:${parts.minute}`,
  };
}

/** Short zone name at a given instant, e.g. "EDT". */
export function timezoneLabel(timeZone, instant = new Date()) {
  const tz = timeZone || DEFAULT_TIMEZONE;
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(instant);
    return parts.find((p) => p.type === "timeZoneName")?.value || tz;
  } catch {
    return tz;
  }
}

function clockTime(instant, timeZone, withMeridiem) {
  const text = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(instant);
  return withMeridiem ? text : text.replace(/\s?[AP]M$/i, "");
}

function meridiem(instant, timeZone) {
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hour12: true })
    .format(instant)
    .replace(/[\d\s]/g, "");
}

/** "Mon, Aug 10 · 3:00 – 3:30 PM EDT" — the one-line summary used everywhere. */
export function formatMeetingWindow(startIso, durationMinutes, timeZone, { withYear = false } = {}) {
  if (!startIso) return "";
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return "";
  const end = new Date(start.getTime() + (durationMinutes || 30) * 60000);
  const tz = timeZone || DEFAULT_TIMEZONE;

  const day = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
  }).format(start);

  // Drop the redundant "PM" from the start time when both ends share it.
  const sameMeridiem = meridiem(start, tz) === meridiem(end, tz);
  return `${day} · ${clockTime(start, tz, !sameMeridiem)} – ${clockTime(end, tz, true)} ${timezoneLabel(tz, start)}`;
}

/** Do [startIso, +duration) and a busy block overlap? */
export function overlaps(startIso, durationMinutes, block) {
  const start = new Date(startIso).getTime();
  const end = start + (durationMinutes || 30) * 60000;
  const bStart = new Date(block.start).getTime();
  const bEnd = new Date(block.end).getTime();
  if ([start, end, bStart, bEnd].some(Number.isNaN)) return false;
  return start < bEnd && bStart < end;
}
